"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { useSession } from "next-auth/react";
import { useTranslations, useLocale, useFormatter } from "next-intl";
import { Link, usePathname } from "@/i18n/navigation";
import {
  badgeLabel,
  lightFor,
  iconFor,
  pickLocalized,
  LIGHT_STYLES,
  type IconName,
  type NotificationItem,
} from "@/lib/notifications-shared";

/** Don't refetch more than once every 30s on incidental triggers. */
const THROTTLE_MS = 30_000;
/** Background poll while the tab is actually being looked at. */
const POLL_MS = 60_000;

type Feed = { unread: number; items: NotificationItem[] };

/**
 * Fetches the guest's notifications. Deliberately client-side: the header reads
 * the session on the client so the layout never touches cookies and public
 * pages stay static (see Header.tsx) — a server component here would undo that.
 */
function useNotifications(enabled: boolean) {
  const [feed, setFeed] = useState<Feed>({ unread: 0, items: [] });
  const lastFetch = useRef(0);
  const abort = useRef<AbortController | null>(null);

  const load = useCallback(
    async (force = false) => {
      if (!enabled) return;
      if (!force && Date.now() - lastFetch.current < THROTTLE_MS) return;
      lastFetch.current = Date.now();
      abort.current?.abort();
      const ctrl = new AbortController();
      abort.current = ctrl;
      try {
        const res = await fetch("/api/notifications", {
          cache: "no-store",
          signal: ctrl.signal,
        });
        if (!res.ok) return;
        const next: Feed = await res.json();
        // An offer dialog is a child of its row: replacing the list while one
        // is open could unmount the row and take the dialog with it. Skip this
        // refresh — the next one lands once the dialog is closed.
        if (document.querySelector("dialog[open]")) return;
        setFeed(next);
      } catch {
        // Offline or aborted — the bell simply keeps what it has.
      }
    },
    [enabled],
  );

  const pathname = usePathname();

  // Effects reach the fetcher through a ref: it keeps the subscriptions below
  // from being torn down and rebuilt every time `load`'s identity changes, and
  // it keeps the effect bodies free of anything that updates state directly.
  const loadRef = useRef(load);
  useEffect(() => {
    loadRef.current = load;
  }, [load]);

  useEffect(() => {
    if (!enabled) return;
    void loadRef.current(true);
    const ctrl = abort;
    return () => ctrl.current?.abort();
  }, [enabled]);

  // An approval or a payment usually lands while the guest is somewhere else,
  // so refetch when they navigate or come back to the tab.
  useEffect(() => {
    if (!enabled) return;
    void loadRef.current();
  }, [pathname, enabled]);

  useEffect(() => {
    if (!enabled) return;
    let timer: number | undefined;
    const start = () => {
      window.clearInterval(timer);
      timer = window.setInterval(() => void loadRef.current(true), POLL_MS);
    };
    const onVisible = () => {
      if (document.visibilityState === "visible") {
        void loadRef.current();
        start();
      } else {
        window.clearInterval(timer);
      }
    };
    const onFocus = () => void loadRef.current();
    if (document.visibilityState === "visible") start();
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onFocus);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onFocus);
    };
  }, [enabled]);

  const markRead = useCallback(async (ids?: string[]) => {
    // Optimistic: the badge clears at once, but the rows keep their unread tint
    // until the panel is reopened so the guest can still see what was new.
    setFeed((f) => ({
      ...f,
      unread: ids ? Math.max(0, f.unread - ids.length) : 0,
    }));
    try {
      const res = await fetch("/api/notifications/read", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(ids ? { ids } : {}),
      });
      if (res.ok) {
        // The server's count wins over both the optimistic guess and any GET
        // that was already in flight when the POST went out.
        const { unread } = (await res.json()) as { unread: number };
        setFeed((f) => ({ ...f, unread }));
      }
    } catch {
      // Left unread server-side; the next fetch restores the true count.
    }
  }, []);

  return { feed, reload: load, markRead };
}

export function NotificationBell({
  variant = "icon",
}: {
  /** "icon" = header button + dropdown; "row" = full-width row for the mobile menu. */
  variant?: "icon" | "row";
}) {
  const { status } = useSession();
  const enabled = status === "authenticated";
  const t = useTranslations("Notifications");
  const { feed, reload, markRead } = useNotifications(enabled);
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Outside click + Escape, same as UserMenu.
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      // An open offer dialog owns Escape; the dropdown behind it must stay.
      if (document.querySelector("dialog[open]")) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  if (!enabled) return null;

  const label = badgeLabel(feed.unread);

  function toggle() {
    const next = !open;
    setOpen(next);
    if (next) {
      // Opening counts as seeing what is on screen — but only that. With more
      // unread than the panel shows, the rest stay unread until the guest opens
      // the full list or presses "Mark all as read". Mark first, refetch after:
      // a refetch racing the mark would bring the old count back.
      const seen = feed.items.filter((i) => !i.read).map((i) => i.id);
      const marked = seen.length > 0 ? markRead(seen) : Promise.resolve();
      void marked.then(() => reload(true));
    }
  }

  const panel = (
    <div className="flex items-center justify-between gap-2 border-b border-sand px-4 py-3">
      <span className="text-sm font-semibold text-bark">{t("title")}</span>
      <button
        type="button"
        onClick={() => void markRead()}
        disabled={feed.unread === 0}
        className="text-xs font-medium text-forest-600 transition-colors hover:text-forest-700 disabled:opacity-40"
      >
        {t("markAllRead")}
      </button>
    </div>
  );

  const list = (
    <NotificationList items={feed.items} onNavigate={() => setOpen(false)} />
  );

  const footer = (
    <div className="border-t border-sand p-1">
      <Link
        href="/account/notifications"
        onClick={() => setOpen(false)}
        className="block rounded-lg px-3 py-2 text-center text-sm font-medium text-forest-600 transition-colors hover:bg-forest-50"
      >
        {t("seeAll")}
      </Link>
    </div>
  );

  if (variant === "row") {
    // Mobile: an absolutely-positioned dropdown inside an already-open menu is
    // awkward, so the list expands inline instead.
    return (
      <div ref={ref}>
        <button
          type="button"
          onClick={toggle}
          aria-expanded={open}
          className="flex w-full items-center justify-between rounded-full border border-sand px-5 py-2.5 text-sm font-medium text-bark transition-colors hover:border-forest-300"
        >
          <span className="flex items-center gap-2">
            <BellIcon />
            {t("title")}
          </span>
          {label && (
            <span className="grid h-5 min-w-5 place-items-center rounded-full bg-forest-500 px-1.5 text-[0.6875rem] font-bold text-white">
              {label}
            </span>
          )}
        </button>
        {open && (
          <div className="mt-2 overflow-hidden rounded-2xl border border-sand bg-cream">
            {panel}
            {list}
            {footer}
          </div>
        )}
      </div>
    );
  }

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={toggle}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={t("aria", { count: feed.unread })}
        className="relative grid h-9 w-9 place-items-center rounded-full text-bark/80 transition-colors hover:bg-forest-50 hover:text-forest-600"
      >
        <BellIcon />
        {label && (
          // Purple, not red or blue: those two already mean "cancelled" and
          // "offer" in the list below.
          <span
            aria-hidden
            className="absolute right-0 top-0 grid h-[1.125rem] min-w-[1.125rem] place-items-center rounded-full bg-forest-500 px-1 text-[0.625rem] font-bold leading-none text-white ring-2 ring-cream"
          >
            {label}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 z-50 mt-2 w-80 max-w-[calc(100vw-2rem)] overflow-hidden rounded-2xl border border-sand bg-cream shadow-lg">
          {panel}
          {list}
          {footer}
        </div>
      )}
    </div>
  );
}

/** The rows themselves — shared by the dropdown, the mobile row and the page. */
export function NotificationList({
  items,
  onNavigate,
  emptyClassName = "px-4 py-8 text-center text-sm text-stone",
}: {
  items: NotificationItem[];
  onNavigate?: () => void;
  emptyClassName?: string;
}) {
  const t = useTranslations("Notifications");

  if (items.length === 0) {
    return <p className={emptyClassName}>{t("empty")}</p>;
  }
  return (
    <div role="menu" className="max-h-96 overflow-y-auto p-1">
      {items.map((item) => (
        <NotificationRow key={item.id} item={item} onNavigate={onNavigate} />
      ))}
    </div>
  );
}

function NotificationRow({
  item,
  onNavigate,
}: {
  item: NotificationItem;
  onNavigate?: () => void;
}) {
  const t = useTranslations("Notifications");
  const tReason = useTranslations("RejectReasons");
  const locale = useLocale();
  const format = useFormatter();
  const dialogRef = useRef<HTMLDialogElement>(null);

  const light = item.light ?? lightFor(item.kind);
  const style = LIGHT_STYLES[light];
  const isOffer = item.category === "OFFER";

  let title: string;
  let body: string;
  if (isOffer) {
    title = pickLocalized(item.title, locale);
    body = pickLocalized(item.body, locale);
  } else {
    const d = item.data ?? {};
    const vars = {
      property: pickLocalized(d.property, locale),
      checkIn: String(d.checkIn ?? ""),
      checkOut: String(d.checkOut ?? ""),
      nights: Number(d.nights ?? 0),
    };
    const reason = typeof d.reason === "string" ? d.reason : "";
    title = t(`${item.kind}Title`);
    body =
      item.kind === "rejected" && reason
        ? t("rejectedReasonBody", { ...vars, reason: tReason(reason) })
        : t(`${item.kind}Body`, vars);
  }

  const content = (
    <>
      {/* Colour is never the only signal (WCAG 1.4.1): the icon differs per
          event and the title states it in words, so the row still reads in
          greyscale. */}
      <span
        aria-hidden
        className={`grid h-8 w-8 shrink-0 place-items-center rounded-full ${style.tint} ${style.icon}`}
      >
        <EventIcon name={iconFor(item.kind)} />
      </span>
      <span className="min-w-0 flex-1">
        <span className="sr-only">
          {isOffer ? t("categoryOffer") : t("categoryBooking")}
          {!item.read ? `, ${t("unread")}` : ""}.{" "}
        </span>
        <span
          className={`block truncate text-sm text-bark ${
            item.read ? "font-medium" : "font-semibold"
          }`}
        >
          {title}
        </span>
        {body && (
          <span className="mt-0.5 line-clamp-2 block text-xs text-stone">
            {body}
          </span>
        )}
        <span className="mt-1 block text-[0.6875rem] text-stone">
          {format.relativeTime(new Date(item.createdAt), new Date())}
        </span>
      </span>
    </>
  );

  const cls = `flex w-full gap-3 rounded-xl px-3 py-2.5 text-left transition-colors hover:bg-forest-50 focus-visible:bg-forest-50 ${
    item.read ? "" : "bg-forest-50/60"
  }`;

  // An offer is the message itself — there is nowhere to "go", and the row is
  // clamped to two lines, so open it in place instead of navigating away.
  if (isOffer) {
    return (
      <>
        <button
          type="button"
          role="menuitem"
          onClick={() => dialogRef.current?.showModal()}
          className={cls}
        >
          {content}
        </button>

        {/* The <dialog> stays a DOM child of the panel on purpose: the bell's
            outside-click check asks whether the click landed inside the panel,
            and showModal() only moves it in the TOP LAYER, not in the tree — so
            the dropdown survives while the offer is open. */}
        <dialog
          ref={dialogRef}
          onClick={(e) => {
            if (e.target === dialogRef.current) dialogRef.current?.close();
          }}
          // Escape closes the offer, not the dropdown behind it.
          onKeyDown={(e) => e.stopPropagation()}
          className="fixed inset-0 m-auto h-fit w-[calc(100%-2rem)] max-w-md rounded-2xl border border-sand bg-cream p-6 text-left shadow-xl backdrop:bg-black/50"
        >
          <div className="flex items-start gap-3">
            <span
              aria-hidden
              className={`grid h-9 w-9 shrink-0 place-items-center rounded-full ${style.tint} ${style.icon}`}
            >
              <EventIcon name={iconFor(item.kind)} />
            </span>
            <div className="min-w-0">
              <h3 className="font-display text-lg font-bold text-bark">
                {title}
              </h3>
              <p className="mt-1 text-xs text-stone">
                {format.relativeTime(new Date(item.createdAt), new Date())}
              </p>
            </div>
          </div>

          {/* whitespace-pre-line: the admin's line breaks are part of the copy. */}
          <p className="mt-4 whitespace-pre-line text-sm leading-relaxed text-bark">
            {body}
          </p>

          <div className="mt-6 flex flex-wrap justify-end gap-3">
            <button
              type="button"
              autoFocus
              onClick={() => dialogRef.current?.close()}
              className="rounded-full border border-sand px-5 py-2 text-sm font-medium text-bark hover:border-forest-300"
            >
              {t("close")}
            </button>
            {item.href && (
              <Link
                href={item.href}
                onClick={() => {
                  dialogRef.current?.close();
                  onNavigate?.();
                }}
                className="rounded-full bg-forest-500 px-5 py-2 text-sm font-medium text-white hover:bg-forest-600"
              >
                {t("offerView")}
              </Link>
            )}
          </div>
        </dialog>
      </>
    );
  }

  if (!item.href) {
    return (
      <div role="menuitem" className={cls}>
        {content}
      </div>
    );
  }
  return (
    <Link role="menuitem" href={item.href} onClick={onNavigate} className={cls}>
      {content}
    </Link>
  );
}

/**
 * One 18px outline icon per event kind. Same stroke weight as the nav icons so
 * the panel sits in the existing visual language; `currentColor` lets the light
 * tint the icon without a second class per variant.
 */
function EventIcon({ name }: { name: IconName }) {
  const common = {
    width: 18,
    height: 18,
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 1.8,
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
    "aria-hidden": true,
  };
  const CALENDAR = "M7 3v3M17 3v3M4 9h16M5 5h14a1 1 0 0 1 1 1v13a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1Z";
  switch (name) {
    case "calendarCheck": // booking approved
      return (
        <svg {...common}>
          <path d={CALENDAR} />
          <path d="m9 14 2 2 4-4" />
        </svg>
      );
    case "calendarX": // cancelled or rejected
      return (
        <svg {...common}>
          <path d={CALENDAR} />
          <path d="m10 13 4 4M14 13l-4 4" />
        </svg>
      );
    case "card": // payment received
      return (
        <svg {...common}>
          <rect x="3" y="5" width="18" height="14" rx="2" />
          <path d="M3 10h18M7 15h3" />
        </svg>
      );
    case "clock": // payment window lapsed
      return (
        <svg {...common}>
          <circle cx="12" cy="12" r="8" />
          <path d="M12 8v4l3 2" />
        </svg>
      );
    case "alert": // the host is asking the guest to act
      return (
        <svg {...common}>
          <path d="M12 4 3 19h18L12 4Z" />
          <path d="M12 10v4M12 17h.01" />
        </svg>
      );
    case "tag": // special offer
      return (
        <svg {...common}>
          <path d="M3 12V5a2 2 0 0 1 2-2h7l9 9-9 9-9-9Z" />
          <circle cx="7.5" cy="7.5" r="1.3" />
        </svg>
      );
  }
}

function BellIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M18 8a6 6 0 1 0-12 0c0 6-2 7-2 7h16s-2-1-2-7"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M13.7 20a2 2 0 0 1-3.4 0"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
    </svg>
  );
}
